import React, { useMemo, FC } from "react";

import { MdsTabBarSectionContent } from "@/design-system/components/tab-bar/MdsTabBarSectionContent";
import { MdsTabBarSection } from "@/design-system/components/tab-bar/types";
import { MdsDropdownButton, MdsDropdownItemKind } from "@/design-system/components/dropdown";
import styled from "@emotion/styled";
import { css } from "@/domains/emotion";
import { mdsSpacings } from "@/design-system/foundations/typography";

export interface MdsTabBarProps {
  selectedItemId: string;
  sections: MdsTabBarSection[];
}

const Container = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  flexWrap: "nowrap",
  justifyContent: "flex-start",
  alignItems: "flex-start",
  maxWidth: 300,
  height: "100%",
  padding: `0 ${theme.spacing[3]}`,

  "@container mds-panel (max-width: 750px)": {
    padding: 0,
    height: "auto",
    margin: `0 auto`,
  },
}));

/**
 * @deprecated - Use the newer MDS components instead.
 *
 * - If something doesn't exist yet, please create a new MDS component and
 *   have it reviewed by the team!
 *
 * - If something needs to hook into our `stores`, add it to the /components directory.
 *
 * - If something is a one-off; please colocate it with the component/page that
 *   needs it.
 *
 * - If you have questions, please ask in the #hub-eng-web-team Slack channel.
 */
export const MdsTabBar: FC<MdsTabBarProps> = ({ selectedItemId, sections }) => {
  const sectionContent = useMemo(
    () =>
      sections.map((section, index) => (
        <MdsTabBarSectionContent
          isFirst={index === 0}
          key={section.id}
          selectedItemId={selectedItemId}
          section={section}
        />
      )),
    [sections, selectedItemId]
  );

  // Create dropdown items from all sections and items
  const dropdownItems = useMemo(() => {
    const items = sections.flatMap((section, sectionIndex) => {
      const sectionItems = section.items.map(item => ({
        id: item.id,
        kind: MdsDropdownItemKind.Button as const,
        label: item.label,
        isChecked: item.id === selectedItemId,
        onClick: item.onClick,
      }));

      // Add section header before items
      const sectionHeader = [
        {
          id: `section-header-${section.id}`,
          kind: MdsDropdownItemKind.Detail as const,
          text: section.label,
          className: sectionIndex > 0 ? sectionHeaderWithPaddingClassName : undefined,
        },
      ];

      return [...sectionHeader, ...sectionItems];
    });
    return { items };
  }, [sections, selectedItemId]);

  // Find the active item label for the dropdown button
  const activeItemLabel = useMemo(() => {
    for (const section of sections) {
      const activeItem = section.items.find(item => item.id === selectedItemId);
      if (activeItem) {
        return activeItem.label;
      }
    }
    return "";
  }, [sections, selectedItemId]);

  return (
    <TabBarContainer>
      <Container>
        <DesktopContent>{sectionContent}</DesktopContent>
        <MobileDropdown
          label={activeItemLabel}
          contentList={dropdownItems}
          dropdownIcon={true}
          offset={-42}
          placement="below-center"
          innerStyles={{ Content: { className: dropdownContentStyles } }}
        />
      </Container>
    </TabBarContainer>
  );
};

const TabBarContainer = styled.div(({ theme }) => ({
  flex: "1 0 280px",
  "@container mds-panel (max-width: 750px)": {
    flex: "unset",
    width: "100%",
    paddingBottom: theme.spacing[2],
    gap: theme.spacing[2],
  },
}));

const DesktopContent = styled.div({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  "@container mds-panel (max-width: 750px)": {
    display: "none",
  },
});

const MobileDropdown = styled(MdsDropdownButton)({
  display: "none",
  width: "100%",
  "> button": {
    width: "100%",
  },
  "@container mds-panel (max-width: 750px)": {
    display: "block",
  },
});

const dropdownContentStyles = css({
  maxWidth: "100%",
  width: "370px",
});

const sectionHeaderWithPaddingClassName = css({
  paddingTop: mdsSpacings()[3],
});
